import {
  Alert,
  Button,
  Center,
  EmptyState,
  DetailField,
  Divider,
  LineItemList,
  Loading,
  MobileHeader,
  MobileScreen,
  Stack,
  Surface,
  Text,
  toneForStatus,
  toast,
  sentenceCase,
} from '@relay-towing/ui';
import { CreditCard, MessageSquare, RefreshCw, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useParams } from 'react-router-dom';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useMarkInvoiceAsPaid } from '../../hooks/useMarkInvoiceAsPaid';
import { useMember } from '../../hooks/useMember';
import { usePaymentLink } from '../../hooks/usePaymentLink';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useRefreshPaymentStatus } from '../../hooks/useRefreshPaymentStatus';
import { companyIdFromProfileOrStorage } from '../../hooks/userCompanies.shared';
import { openExternal, share } from '../../lib/nativeBridge';
import { useBack } from '../shell/useBack';

const money = (amount: number) =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** What a refusal said, or what was being tried. */
const failure = (error: unknown, fallback: string) => {
  const { response, message } = error as {
    response?: { data?: { message?: unknown } };
    message?: string;
  };
  const said = response?.data?.message;
  return typeof said === 'string' ? said : message || fallback;
};

/**
 * Taking payment for a call's invoice, opened from the call's Invoice
 * section with "Take payment", or from the create sheet once the invoice is
 * made. There is no Figma frame for it: it follows the old payment screen,
 * laid out as InvoiceDetailScreen reads an invoice. The total heads it, then
 * the invoice's status, customer and payment, its charges, and what is left
 * to do.
 *
 * Paid by cash, the member collects it and marks it paid. By card, the
 * payment link's QR code is shown for the customer to scan, with the link
 * to share through the phone's share sheet and, with "Send invoices by
 * SMS", to text from the phone's messages; the status is refreshed from the
 * provider, whose word comes first, as its webhook lands before the invoice
 * is marked paid. Without a payment provider connected a card invoice
 * cannot be collected here, and the screen says so. Without "Take payment"
 * it says who can collect instead of offering buttons the server refuses.
 */
export function CallPaymentScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBack(`/calls/${id}`);
  const member = useMember();
  const { data: profile } = useDriverProfile();
  const companyId = companyIdFromProfileOrStorage(profile) || undefined;
  const status = usePaymentStatus(id, companyId);
  const link = usePaymentLink(id, companyId);
  const refresh = useRefreshPaymentStatus(id, companyId);
  const markPaid = useMarkInvoiceAsPaid(id ?? '', companyId);

  const payment = status.data;
  const invoice = payment?.invoice ?? null;
  // The provider's word on a card payment where there is one, else the
  // invoice's own status.
  const providerStatus = invoice
    ? payment?.paymentProvider === 'ROADSYNC'
      ? invoice.roadsyncPaymentStatus
      : invoice.squarePaymentStatus
    : undefined;
  const shownStatus = invoice ? providerStatus || invoice.status : undefined;
  const header = (
    <MobileHeader
      onBack={back}
      backLabel="Back to the call"
      status={
        shownStatus
          ? {
              label: sentenceCase(shownStatus),
              tone: toneForStatus(shownStatus),
            }
          : undefined
      }
      title={shownStatus ? undefined : 'Payment'}
    />
  );

  if (status.isLoading || (!payment && !status.isError))
    return <Loading label="Loading the payment" fill="screen" />;
  if (!payment)
    return (
      <MobileScreen header={header}>
        <EmptyState
          title="The payment could not be loaded"
          description="Check the connection and try again."
          action={<Button onClick={() => status.refetch()}>Try again</Button>}
        />
      </MobileScreen>
    );
  if (!invoice)
    return (
      <MobileScreen header={header}>
        <EmptyState
          title="This call has no invoice"
          description="Create the invoice from the call first."
          action={<Button onClick={back}>Back to the call</Button>}
        />
      </MobileScreen>
    );

  const takesPayment = member.can('invoices.take_payment');
  const paid = providerStatus === 'COMPLETED' || invoice.status === 'PAID';

  const makeLink = async () => {
    try {
      await link.make(invoice.id);
      await status.refetch();
    } catch (error) {
      toast.error(failure(error, 'The payment link could not be made.'));
    }
  };

  const shareLink = async (url: string) => {
    const said = await share({
      title: 'Payment link',
      text: `Please pay ${money(invoice.total)} for call ${payment.dispatchNumber}`,
      url,
    });
    if (said === 'copied') toast.success('Payment link copied');
    if (said === 'failed') toast.error('The payment link could not be shared');
  };

  const refreshStatus = async () => {
    try {
      await refresh.mutateAsync(invoice.id);
      await status.refetch();
      toast.success('Payment status refreshed');
    } catch (error) {
      toast.error(failure(error, 'The payment status could not be refreshed.'));
    }
  };

  const markAsPaid = async () => {
    try {
      await markPaid.mutateAsync(invoice.id);
      await status.refetch();
      toast.success('Invoice marked as paid');
    } catch (error) {
      toast.error(failure(error, 'The invoice could not be marked paid.'));
    }
  };

  const collect = () => {
    if (paid)
      return (
        <Alert tone="success" title="Payment received">
          {money(invoice.total)} has been paid.
        </Alert>
      );
    // Said nothing until the role is known, so a member who may collect
    // is not told for a moment that they may not.
    if (member.isLoading) return null;
    if (!takesPayment)
      return (
        <Alert tone="neutral" title="Invoice created">
          Taking payment needs the take payment permission. An owner or admin
          can collect it.
        </Alert>
      );
    if (invoice.isCashPayment)
      return (
        <Stack gap={3}>
          <Alert tone="warning" title="Cash payment">
            Collect {money(invoice.total)} in cash, then mark it paid.
          </Alert>
          <Button fullWidth loading={markPaid.isPending} onClick={markAsPaid}>
            Mark as paid
          </Button>
        </Stack>
      );
    if (!payment.paymentConnected)
      return (
        <EmptyState
          icon={CreditCard}
          title="Payments not connected"
          description="Your company needs to connect a payment provider to accept payments. Please contact your administrator."
        />
      );
    if (link.making) return <Loading label="Making the payment link" />;
    if (!link.link)
      return (
        <Stack gap={3}>
          <Alert tone="neutral" title="No payment link on this phone">
            Make one to show the customer its code or send it to them.
          </Alert>
          <Button fullWidth onClick={makeLink}>
            Make a payment link
          </Button>
        </Stack>
      );
    return (
      <Stack gap={3}>
        <Center direction="column" gap={3}>
          <Surface bordered radius="lg" padding="md">
            <QRCodeSVG value={link.link} size={180} level="H" includeMargin />
          </Surface>
          <Text variant="body-sm" tone="muted" align="center">
            Scan to pay {money(invoice.total)}
          </Text>
        </Center>
        <Button
          fullWidth
          leadingIcon={<Share2 />}
          onClick={() => shareLink(link.link!)}
        >
          Share payment link
        </Button>
        {member.can('invoices.send_sms') ? (
          <Button
            variant="outline"
            fullWidth
            leadingIcon={<MessageSquare />}
            onClick={() =>
              openExternal(
                `sms:?body=${encodeURIComponent(`Please pay your invoice: ${link.link}`)}`
              )
            }
          >
            Text the link
          </Button>
        ) : null}
        <Button
          variant="outline"
          fullWidth
          leadingIcon={<RefreshCw />}
          loading={refresh.isPending}
          onClick={refreshStatus}
        >
          Refresh payment status
        </Button>
      </Stack>
    );
  };

  return (
    <MobileScreen header={header}>
      {/* Laid out as the invoice reads (InvoiceDetailScreen): its total and
          who it is for, then Details and Charges, ruled off; the status is
          the header's chip. Then what to do about the payment. */}
      <Stack gap={6}>
        <Stack gap={1}>
          <Text as="h1" variant="h1" numeric>
            {money(invoice.total)}
          </Text>
          {payment.contactName ? (
            <Text tone="muted">{payment.contactName}</Text>
          ) : null}
          <Text tone="muted">{invoice.invoiceNumber}</Text>
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Details
          </Text>
          <DetailField
            label="Payment"
            value={invoice.isCashPayment ? 'Cash' : 'Card'}
          />
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Charges
          </Text>
          <LineItemList
            items={invoice.items.map((item) => ({
              description: item.description,
              qty: item.quantity,
              rate: item.unitPrice,
            }))}
            total={invoice.total}
          />
        </Stack>
        <Divider />
        {collect()}
      </Stack>
    </MobileScreen>
  );
}
